// components/forms/Formik.tsx

import { Formik as OriginalFormik, FormikProps, FormikConfig, FieldArray, useField, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setFormClean, setFormDirty } from 'store/reducers/formslice';

/**
 * Custom Formik wrapper component
 *
 * This component wraps the original Formik component and applies a global unsaved changes warning
 * using the useUnsavedChangesWarning hook. This is designed to ensure that users are alerted if they
 * attempt to navigate away from a form with unsaved changes. It allows developers to maintain consistency
 * across all forms in the application without needing to manually add the warning logic to each form.
 *
 * The component also re-exports common Formik utilities such as FormikHelpers, FieldArray, useField,
 * and useFormikContext for ease of use throughout the application.
 */
const Formik = <Values extends {}>({
  children,
  skipFormCleanOnUnMount = false,
  ...props
}: FormikConfig<Values> & { skipFormCleanOnUnMount?: boolean }) => {
  const FormikWrapper = (formikBag: FormikProps<Values>) => {
    const dispatch = useDispatch();

    useEffect(() => {
      if (formikBag.dirty) {
        dispatch(setFormDirty());
      } else {
        dispatch(setFormClean());
      }

      return () => {
        if (!skipFormCleanOnUnMount) dispatch(setFormClean());
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikBag.dirty]);

    return typeof children === 'function' ? children(formikBag) : children;
  };

  return <OriginalFormik {...props}>{FormikWrapper}</OriginalFormik>;
};

// Re-export Formik utilities
export type { FormikHelpers } from 'formik'; // Export the FormikHelpers type correctly
export { FieldArray, useField, useFormikContext }; // Re-export other commonly used Formik utilities

// Named export of the custom Formik component
export { Formik };

// Default export of the custom Formik component
export default Formik;
